import React, { useState, useEffect } from 'react';
import { websocketService } from '../services/websocketService';

interface QuestionEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  originalQuestion: string;
  onQuestionEdited: (originalQuestion: string, editedQuestion: string, answer: string) => void;
}

const QuestionEditModal: React.FC<QuestionEditModalProps> = ({
  isOpen,
  onClose,
  originalQuestion,
  onQuestionEdited,
}) => {
  const [editedQuestion, setEditedQuestion] = useState(originalQuestion);
  const [isGenerating, setIsGenerating] = useState(false);
  const [answer, setAnswer] = useState<string>('');
  const [error, setError] = useState<string>('');

  // Reset state when modal opens with new question
  useEffect(() => {
    if (isOpen) {
      setEditedQuestion(originalQuestion);
      setAnswer('');
      setError('');
      setIsGenerating(false);
    }
  }, [isOpen, originalQuestion]);

  const handleQuestionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedQuestion(e.target.value);
    setAnswer('');
    setError('');
  };

  const generateAnswer = async () => {
    if (!editedQuestion.trim()) {
      setError('Please enter a question');
      return;
    }

    setIsGenerating(true);
    setError('');
    setAnswer('');

    try {
      // Send edited question to backend via WebSocket
      websocketService.sendMessage({
        type: 'edit_question',
        data: {
          original_question: originalQuestion,
          edited_question: editedQuestion,
          timestamp: new Date().toISOString(),
        },
      });

      // Set up a timeout for the response
      const timeout = setTimeout(() => {
        setIsGenerating(false);
        setError('Request timed out. Please try again.');
      }, 30000); // 30 second timeout

      // Listen for the response using custom event
      const handleEditResponse = (event: CustomEvent) => {
        const responseData = event.detail;
        if (responseData.original_question === originalQuestion) {
          clearTimeout(timeout);
          setIsGenerating(false);
          
          if (responseData.error) {
            setError(responseData.error);
          } else {
            setAnswer(responseData.answer);
          }
          
          // Remove the event listener
          window.removeEventListener('editQuestionResponse', handleEditResponse as EventListener);
        }
      };

      // Add event listener for the response
      window.addEventListener('editQuestionResponse', handleEditResponse as EventListener);

    } catch (error) {
      setIsGenerating(false);
      setError('Failed to send request. Please check your connection.');
    }
  };

  const handleSave = () => {
    if (answer) {
      onQuestionEdited(originalQuestion, editedQuestion, answer);
      onClose();
    }
  };

  const formatAnswer = (answerText: string) => {
    const lines = answerText.split('\n');
    const formattedLines = lines.map((line, index) => {
      if (line.trim().startsWith('•') || line.trim().startsWith('-') || line.trim().startsWith('*')) {
        return (
          <li key={index} className="ml-3 mb-1 text-sm">
            {line.trim().substring(1).trim()}
          </li>
        );
      }
      if (line.trim()) {
        return (
          <p key={index} className="mb-2 text-sm">
            {line.trim()}
          </p>
        );
      }
      return null;
    }).filter(Boolean);

    return <div>{formattedLines}</div>;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-blue-50 px-6 py-4 border-b border-blue-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center">
              <span className="mr-2">✏️</span>
              Edit Question
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Original Question */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Original Question:
            </label>
            <div className="bg-gray-100 rounded-lg p-3 text-sm text-gray-600">
              {originalQuestion}
            </div>
          </div>

          {/* Edited Question */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Edit Question:
            </label>
            <textarea
              value={editedQuestion}
              onChange={handleQuestionChange}
              className="w-full h-24 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Edit the question here..."
            />
          </div>

          {/* Generate Answer Button */}
          <div className="flex justify-center">
            <button
              onClick={generateAnswer}
              disabled={isGenerating || !editedQuestion.trim()}
              className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center"
            >
              {isGenerating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Generating Answer...
                </>
              ) : (
                <>
                  <span className="mr-2">🤖</span>
                  Generate Answer
                </>
              )}
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center text-red-700">
                <span className="mr-2">❌</span>
                <span className="text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Generated Answer */}
          {answer && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-green-800 flex items-center">
                  <span className="mr-2">💡</span>
                  Generated Answer:
                </h3>
                <div className="text-xs text-green-600">
                  Real-time AI response
                </div>
              </div>
              <div className="text-gray-800 prose prose-sm max-w-none">
                {formatAnswer(answer)}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!answer}
            className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionEditModal; 